import { Chess } from "@vendor/chess.js";
import type { HistoryEntry } from "./session.ts";
import { positionKey } from "../chess/positionKey.ts";
import { teachingMoveFor } from "../lessons/repository.ts";
import type { LessonCourse } from "../lessons/schema.ts";

export type BoardOverlay =
  "center" | "threatened" | "hero" | "reserve" | "loose" | "lastMove" | "pins" | "pawns" | "safe";

export type BoardOverlayState = Record<BoardOverlay, boolean>;

export type ActorRoles = {
  hero: string[];
  reserve: string[];
};

export type CenterControl = {
  tiers: Record<string, number>;
  whiteTotal: number;
  blackTotal: number;
  whiteSquares: number;
  blackSquares: number;
  contestedSquares: number;
  net: number;
};

export const DEFAULT_BOARD_OVERLAYS: BoardOverlayState = {
  center: false,
  threatened: false,
  hero: false,
  reserve: false,
  loose: false,
  lastMove: false,
  pins: false,
  pawns: false,
  safe: false,
};

export const CENTRAL_ZONE = [
  "c3",
  "c4",
  "c5",
  "c6",
  "d3",
  "d4",
  "d5",
  "d6",
  "e3",
  "e4",
  "e5",
  "e6",
  "f3",
  "f4",
  "f5",
  "f6",
] as const;

/**
 * The pieces that carry each opening's identity and plan, by starting square;
 * currentSquaresForOrigins follows each piece as it moves. A hero piece is
 * never faded as a reserve piece.
 */
export const HERO_ROLES: Record<string, string[]> = {
  // Black courses: the pawns and pieces each defence is named after.
  caroKann: ["c7", "d7", "c8"], // ...c6, ...d5, the bishop that comes out before ...e6
  qgdBlack: ["d7", "e7", "g8"], // ...d5, ...e6, ...Nf6
  symmetricalEnglish: ["c7", "f8", "b8"], // ...c5, ...Bg7, ...Nc6
  openGame: ["e7", "b8", "f8"], // ...e5, ...Nc6, ...Bc5
  sicilianNajdorf: ["c7", "d7", "a7"], // ...c5, ...d6, ...a6
  kingsIndian: ["g8", "f8", "g7", "e7"], // ...Nf6, ...Bg7, ...g6 and the ...e5 break
  frenchDefense: ["e7", "d7", "c7"], // ...e6, ...d5 and the ...c5 break
  kingsEnglish: ["e7", "g8", "d7"], // ...e5, ...Nf6 and the ...d5 strike
  petroffDefense: ["e7", "g8", "d7"], // ...e5, ...Nf6 and the ...d6 kick
  philidorDefense: ["e7", "d7", "b8"], // ...e5, ...d6 and the ...Nbd7 support
  nimzoIndian: ["g8", "e7", "f8"], // ...Nf6, ...e6 and the bishop that pins on b4
  slavDefense: ["d7", "c7", "c8"], // ...d5, ...c6 and the bishop that leaves before ...e6
  scandinavianDefense: ["d7", "d8", "c8"], // ...d5, the queen that recaptures, the bishop White chases
  pircDefense: ["d7", "g8", "f8"], // ...d6, ...Nf6 and the bishop on g7
  qgaBlack: ["d7", "g8", "c8"], // ...d5, ...Nf6 and the light-squared bishop
  twoKnights: ["e7", "b8", "g8"], // ...e5, ...Nc6 and ...Nf6
  englishNf6: ["g8", "g7", "e7"], // ...Nf6, ...g6 and the ...e5 break
  retiBlack: ["d7", "e7", "g8"], // ...d5, ...e6 and ...Nf6
  classicalSicilian: ["c7", "b8", "e7"], // ...c5, ...Nc6 and ...e5
  kanSicilian: ["c7", "e7", "a7"], // ...c5, ...e6 and ...a6
  berlinDefense: ["e7", "b8", "g8"], // ...e5, ...Nc6 and ...Nf6
  steinitzDefense: ["e7", "d7", "b8"], // ...e5, ...d6 and ...Nc6
  classicalRuy: ["e7", "b8", "f8"], // ...e5, ...Nc6 and ...Bc5
  // White courses.
  italian: ["e2", "g1", "f1"], // e4, Nf3, Bc4
  quietTwoKnights: ["e2", "d2", "f1"], // e4, the d3 pawn that meets ...Nf6, and Bc4
  queensGambit: ["d2", "c2", "c1"], // d4, c4, Bg5
  ruyLopez: ["e2", "g1", "f1"], // e4, Nf3, the Spanish bishop
  english: ["c2", "b1", "f1"], // c4, Nc3, Bg2
  london: ["d2", "c1", "e2"], // d4, Bf4, e3
  alapin: ["c2", "d2", "g1"], // c3, d4, Nf3 behind the centre
  scotch: ["g1", "d2", "e2"], // Nf3, the d4 push and the e-pawn that goes to e5
  fourKnights: ["g1", "b1", "f1"], // Nf3, Nc3 and the Bb5 that pressures the e5 defender
  reti: ["g1", "c2", "f1"], // Nf3, the c4 flank strike and the Bg2 fianchetto
  openSicilian: ["g1", "d2", "b1"], // Nf3, the d4 break and the Nc3 that guards e4
  kingsGambit: ["f2", "g1", "c1"], // the f4 gambit pawn, Nf3 covering h4 and the Bxf4 that wins it back
  pircModern: ["e2", "d2", "b1"], // e4, d4 and the Nc3 that defends e4
  frenchAdvance: ["e2", "c2", "b2"], // the e5 push, the c3 pawn that backs d4 and the b4 push for space
  scandinavian: ["e2", "b1", "h2"], // exd5, the Nc3 that hits the queen and the h-pawn that chases the bishop
  caroAdvance: ["e2", "c2", "d1"], // the e5 push, the c4 break and the Qa4 pin
  vienna: ["b1", "f2", "g1"], // Nc3, the f4 push and the Nf3 that covers h4
};

/** How many of the student's own moves ahead the course plan is read for the reserve fade. */
export const PLAN_LOOKAHEAD = 5;

const STORAGE_KEYS: Record<BoardOverlay, string[]> = {
  center: ["f15m_overlay_center"],
  threatened: ["f15m_overlay_threatened", "f15m_overlay_threats"],
  // Keep the former key so returning users retain their old preference.
  hero: ["f15m_overlay_hero", "f15m_overlay_leads"],
  reserve: ["f15m_overlay_reserve", "f15m_overlay_reserves"],
  loose: ["f15m_overlay_loose"],
  lastMove: ["f15m_overlay_last_move"],
  pins: ["f15m_overlay_pins"],
  pawns: ["f15m_overlay_pawns"],
  safe: ["f15m_overlay_safe"],
};

type ReadableStorage = Pick<Storage, "getItem">;
type WritableStorage = Pick<Storage, "setItem">;

export function loadBoardOverlays(storage?: ReadableStorage | null): BoardOverlayState {
  const source = storage ?? (typeof window === "undefined" ? null : window.localStorage);
  if (!source) return { ...DEFAULT_BOARD_OVERLAYS };
  const enabled = (key: BoardOverlay) => {
    try {
      return STORAGE_KEYS[key].some((name) => source.getItem(name) === "1");
    } catch {
      return false;
    }
  };
  return Object.fromEntries(
    (Object.keys(DEFAULT_BOARD_OVERLAYS) as BoardOverlay[]).map((key) => [key, enabled(key)])
  ) as BoardOverlayState;
}

export function saveBoardOverlay(
  key: BoardOverlay,
  enabled: boolean,
  storage?: WritableStorage | null
): void {
  const target = storage ?? (typeof window === "undefined" ? null : window.localStorage);
  if (!target) return;
  try {
    target.setItem(STORAGE_KEYS[key][0], enabled ? "1" : "0");
  } catch {
    /* Storage can be unavailable in private or restricted browsing contexts. */
  }
}

function pieceAtFen(fen: string | undefined, square: string): string | null {
  if (!fen || !/^[a-h][1-8]$/.test(square)) return null;
  const placement = fen.split(" ")[0];
  const ranks = placement?.split("/");
  if (!ranks || ranks.length !== 8) return null;
  const targetFile = square.charCodeAt(0) - 97;
  const targetRank = Number(square[1]);
  const rankText = ranks[8 - targetRank];
  let file = 0;
  for (const char of rankText) {
    if (/\d/.test(char)) {
      file += Number(char);
    } else {
      if (file === targetFile) return char;
      file += 1;
    }
  }
  return null;
}

/** Track opening-role pieces by origin through moves, captures and castling. */
export function currentSquaresForOrigins(
  history: Pick<HistoryEntry, "uci" | "ply" | "fenBefore">[],
  originSquares: string[]
): string[] {
  const tracks = new Map(originSquares.map((square) => [square, square]));

  for (const entry of history) {
    const from = entry.uci.slice(0, 2);
    const to = entry.uci.slice(2, 4);
    if (!/^[a-h][1-8]$/.test(from) || !/^[a-h][1-8]$/.test(to)) continue;

    // Normal captures remove a tracked piece occupying the destination.
    for (const [origin, current] of tracks) {
      if (current === to) tracks.delete(origin);
    }

    // En passant captures a pawn beside the destination, not on it.
    const mover = pieceAtFen(entry.fenBefore, from)?.toLowerCase();
    const destinationWasEmpty = pieceAtFen(entry.fenBefore, to) == null;
    if (mover === "p" && from[0] !== to[0] && destinationWasEmpty) {
      const capturedSquare = `${to[0]}${from[1]}`;
      for (const [origin, current] of tracks) {
        if (current === capturedSquare) tracks.delete(origin);
      }
    }

    for (const [origin, current] of tracks) {
      if (current === from) tracks.set(origin, to);
    }

    // A castling move also relocates a rook that may be a reserve actor. Only
    // a king castles: a queen or rook going e1-g1 leaves the h1 rook alone.
    const castles = mover === "k" && /^e[18]$/.test(from);
    if (castles && to === `g${from[1]}`) {
      for (const [origin, current] of tracks) {
        if (current === `h${from[1]}`) tracks.set(origin, `f${from[1]}`);
      }
    }
    if (castles && to === `c${from[1]}`) {
      for (const [origin, current] of tracks) {
        if (current === `a${from[1]}`) tracks.set(origin, `d${from[1]}`);
      }
    }
  }

  return [...tracks.values()];
}

/** Where each hero piece of the opening stands now. */
export function heroSquares(
  openingId: string,
  history: Pick<HistoryEntry, "uci" | "ply" | "fenBefore">[]
): string[] {
  return currentSquaresForOrigins(history, HERO_ROLES[openingId] || []);
}

type PlanCourse = Pick<LessonCourse, "nodes">;
type Square = Parameters<Chess["get"]>[0];

/** The side's pieces with their squares. */
function sidePieces(chess: Chess, side: "w" | "b"): Array<{ square: string; type: string }> {
  const out: Array<{ square: string; type: string }> = [];
  for (const file of "abcdefgh")
    for (const rank of "12345678") {
      const square = `${file}${rank}`;
      const piece = chess.get(square as Square);
      if (piece && piece.color === side) out.push({ square, type: piece.type });
    }
  return out;
}

/**
 * The squares, as they stand now, of the side's pieces that the course's own
 * line moves within its next `lookahead` moves, and how many of the side's
 * moves the line covered before it ended.
 */
export function plannedSquares(
  course: PlanCourse | null | undefined,
  fen: string,
  side: "w" | "b",
  lookahead = PLAN_LOOKAHEAD
): { squares: Set<string>; covered: number } {
  const squares = new Set<string>();
  if (!course) return { squares, covered: 0 };
  const chess = new Chess(fen);
  // Future square -> square of the same piece now.
  const trace = new Map<string, string>();
  for (const piece of sidePieces(chess, side)) trace.set(piece.square, piece.square);
  let covered = 0;
  for (let ply = 0; ply < 2 * lookahead + 1 && covered < lookahead; ply += 1) {
    const move = teachingMoveFor(course.nodes[positionKey(chess.fen())]);
    if (!move) break;
    let played: ReturnType<Chess["move"]> | null = null;
    try {
      played = chess.move(move.san);
    } catch {
      played = null;
    }
    if (!played) break;
    if (played.color === side) {
      covered += 1;
      const now = trace.get(played.from);
      trace.delete(played.from);
      if (now) {
        squares.add(now);
        trace.set(played.to, now);
      }
      // Castling brings the rook into play as well.
      const rank = played.from[1];
      const flags = played.flags || "";
      const rookFrom = flags.includes("k") ? `h${rank}` : flags.includes("q") ? `a${rank}` : null;
      const rookTo = flags.includes("k") ? `f${rank}` : `d${rank}`;
      if (rookFrom) {
        const rook = trace.get(rookFrom);
        trace.delete(rookFrom);
        if (rook) {
          squares.add(rook);
          trace.set(rookTo, rook);
        }
      }
    } else {
      trace.delete(played.to);
    }
  }
  return { squares, covered };
}

const HOME_MINORS: Record<"w" | "b", Record<string, string>> = {
  w: { b1: "n", g1: "n", c1: "b", f1: "b" },
  b: { b8: "n", g8: "n", c8: "b", f8: "b" },
};

/** Starting squares of the pieces that can be held in reserve. */
const HOME_PIECES: Record<"w" | "b", Record<string, string>> = {
  w: { ...HOME_MINORS.w, a1: "r", h1: "r", d1: "q" },
  b: { ...HOME_MINORS.b, a8: "r", h8: "r", d8: "q" },
};

/**
 * Pieces to fade as "keep in reserve": the side's knights, bishops, rooks and
 * queen still on their starting squares that the course's own line does not
 * use in its next PLAN_LOOKAHEAD moves, so a piece the coach is about to ask for is never faded. Where the
 * course has no line (off the course, or the line ends sooner), only the
 * queen and rooks are faded, and only while a minor piece is still at home,
 * as the board coach says: develop the minor pieces first. A hero piece, and
 * any piece the coach recommends now, is never faded.
 */
export function reserveSquares(args: {
  course: PlanCourse | null | undefined;
  fen: string;
  side: "w" | "b";
  hero?: string[];
  recommended?: string[];
  lookahead?: number;
}): string[] {
  const { fen, side } = args;
  const lookahead = args.lookahead ?? PLAN_LOOKAHEAD;
  const chess = new Chess(fen);
  const keep = new Set([...(args.hero || []), ...(args.recommended || [])]);
  const plan = plannedSquares(args.course, fen, side, lookahead);
  // Only a piece not yet brought into play is held in reserve: a developed
  // piece the course leaves alone for a while is not faded.
  const pieces = sidePieces(chess, side)
    .filter((piece) => HOME_PIECES[side][piece.square] === piece.type)
    .filter((piece) => !keep.has(piece.square) && !plan.squares.has(piece.square));
  if (plan.covered >= lookahead) return pieces.map((piece) => piece.square);
  const minorsHome = Object.entries(HOME_MINORS[side]).some(([square, type]) => {
    const piece = chess.get(square as Square);
    return piece?.color === side && piece.type === type;
  });
  if (!minorsHome) return [];
  return pieces.filter((piece) => piece.type === "q" || piece.type === "r").map((p) => p.square);
}

export type Attacker = { square: string; type: string };

const FILES = "abcdefgh";
const ROOK_DIRECTIONS = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];
const BISHOP_DIRECTIONS = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];
const KNIGHT_HOPS = [
  [2, 1],
  [2, -1],
  [-2, 1],
  [-2, -1],
  [1, 2],
  [1, -2],
  [-1, 2],
  [-1, -2],
];

/**
 * Every square each side attacks or defends, with the pieces doing it. Raw
 * piece influence, not legal moves: a pawn guards diagonally whatever stands
 * there, and a piece defends its own side's pieces. Sliders stop at the first
 * piece (no x-rays).
 */
export function attackMap(fen: string): Record<"w" | "b", Record<string, Attacker[]>> {
  const map: Record<"w" | "b", Record<string, Attacker[]>> = { w: {}, b: {} };
  let board: Array<Array<{ color: string; type: string } | null>>;
  try {
    board = new Chess(fen).board();
  } catch {
    return map;
  }
  const inBounds = (file: number, rank: number) => file >= 0 && file < 8 && rank >= 0 && rank < 8;
  const pieceAt = (file: number, rank: number) => board[7 - rank]?.[file] ?? null;
  const add = (color: "w" | "b", file: number, rank: number, from: Attacker) => {
    const square = `${FILES[file]}${rank + 1}`;
    (map[color][square] ||= []).push(from);
  };
  for (let file = 0; file < 8; file += 1) {
    for (let rank = 0; rank < 8; rank += 1) {
      const piece = pieceAt(file, rank);
      if (!piece) continue;
      const color = piece.color as "w" | "b";
      const from = { square: `${FILES[file]}${rank + 1}`, type: piece.type };
      const step = (offsets: number[][]) => {
        for (const [df, dr] of offsets)
          if (inBounds(file + df, rank + dr)) add(color, file + df, rank + dr, from);
      };
      const slide = (directions: number[][]) => {
        for (const [df, dr] of directions) {
          let f = file + df;
          let r = rank + dr;
          while (inBounds(f, r)) {
            add(color, f, r, from);
            if (pieceAt(f, r)) break;
            f += df;
            r += dr;
          }
        }
      };
      if (piece.type === "p") {
        const forward = color === "w" ? 1 : -1;
        step([
          [1, forward],
          [-1, forward],
        ]);
      } else if (piece.type === "n") step(KNIGHT_HOPS);
      else if (piece.type === "b") slide(BISHOP_DIRECTIONS);
      else if (piece.type === "r") slide(ROOK_DIRECTIONS);
      else if (piece.type === "q") slide([...ROOK_DIRECTIONS, ...BISHOP_DIRECTIONS]);
      else if (piece.type === "k") step([...ROOK_DIRECTIONS, ...BISHOP_DIRECTIONS]);
    }
  }
  return map;
}

/** Weight of one attacker on a central square: a pawn's guard counts double. */
const controlWeight = (attacker: Attacker) => (attacker.type === "p" ? 2 : 1);

/**
 * Central control across the 4×4 zone. Each side scores a square by its
 * attackers (a pawn counts double: a square a pawn guards is hard for the
 * other side to use) plus one for a piece or pawn standing on it. Each
 * square's tier is the difference, capped at ±5; the net is the sum of tiers.
 */
export function computeCenterControl(fen: string): CenterControl {
  const attacks = attackMap(fen);
  let chess: Chess | null = null;
  try {
    chess = new Chess(fen);
  } catch {
    chess = null;
  }
  const tiers: Record<string, number> = {};
  let whiteTotal = 0;
  let blackTotal = 0;
  let whiteSquares = 0;
  let blackSquares = 0;
  let contestedSquares = 0;
  let net = 0;
  for (const square of CENTRAL_ZONE) {
    const occupant = chess?.get(square as Square);
    const occupies = (color: "w" | "b") =>
      occupant && occupant.color === color && occupant.type !== "k" ? 1 : 0;
    const white =
      (attacks.w[square] || []).reduce((sum, a) => sum + controlWeight(a), 0) + occupies("w");
    const black =
      (attacks.b[square] || []).reduce((sum, a) => sum + controlWeight(a), 0) + occupies("b");
    whiteTotal += white;
    blackTotal += black;
    const tier = Math.max(-5, Math.min(5, white - black));
    tiers[square] = tier;
    net += tier;
    if (tier > 0) whiteSquares += 1;
    else if (tier < 0) blackSquares += 1;
    else contestedSquares += 1;
  }
  return { tiers, whiteTotal, blackTotal, whiteSquares, blackSquares, contestedSquares, net };
}

export function centerControlVerdict(net: number): {
  text: string;
  tone: "white" | "black" | "neutral";
} {
  if (net <= -8) return { text: "Black is dominating the center.", tone: "black" };
  if (net <= -4) return { text: "Black is holding the center.", tone: "black" };
  if (net <= -2) return { text: "Black edges the center.", tone: "black" };
  if (net < 2) return { text: "The center is evenly contested.", tone: "neutral" };
  if (net < 4) return { text: "White edges the center.", tone: "white" };
  if (net < 8) return { text: "White is holding the center.", tone: "white" };
  return { text: "White is dominating the center.", tone: "white" };
}

/** Return White-occupied squares Black can legally capture and attacker counts. */
export function computeThreatenedSquares(
  fen: string,
  playerSide: "w" | "b" = "w"
): Record<string, number> {
  const parts = fen.split(" ");
  if (parts.length < 6) return {};
  parts[1] = playerSide === "w" ? "b" : "w";
  parts[3] = "-";
  let probe: Chess;
  try {
    probe = new Chess(parts.join(" "));
  } catch {
    return {};
  }
  const counts: Record<string, number> = {};
  for (const move of probe.moves({ verbose: true })) {
    if (!String(move.flags || "").includes("c")) continue;
    counts[move.to] = (counts[move.to] || 0) + 1;
  }
  return counts;
}
